import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { AqilaButton } from '@agro1desenvolvimento/agro1-design-system/aqila';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agro1desenvolvimento/agro1-design-system/components/ui';
import { PropriedadesService, SafrasService } from '../../../services';
import RelatorioVisitaService, {
  type RelatorioVisitaCreated,
} from '../../../services/relatorio-visita';
import useAsyncFetch from '../../../hooks/use-async';
import useLocalStore from '../../../hooks/use-local-store';
import Agro1Loader from '../../../components/Agro1Loader';
import Agro1Toast from '../../../components/Agro1Toast';
import DateField from './date-field';

const DATE_KEY_FORMAT = 'YYYY-MM-DD';
const DEFAULT_PROXIMA_VISITA_DIAS = 7;
const SAFRA_CURRENT_SUFFIX = ' (atual)';

// Tipos de histórico que compõem os "pontos" do corpo do relatório de
// visita v2 (RelatorioVisitaV2::Publico::Queries::TalhoesQuery#pontos_for,
// api-agro1). Recomendações e aplicações (pulverização) ficam de fora: têm
// seção própria e não são pontos de visita.
const OPERACAO_DATAS_TIPOS =
  'stand,produtividade,inseto,invasora,doenca,nematoide,inimigo_natural,climatica,solo,manutencao_melhoria,anotacao,colheita_afericao,semeadura_afericao,aplicacao_afericao';
const APP_BASENAME = '/aqila-2';

const getCreateErrorMessage = (error: unknown): string => {
  const apiError = isAxiosError(error)
    ? error.response?.data?.error
    : undefined;
  if (typeof apiError === 'string') return apiError;

  return 'Falha ao gerar o relatório de visita.';
};

const RelatorioVisitaCreatePage = () => {
  const navigate = useNavigate();

  const { value: currentPropriedade } = useLocalStore('currentPropriedade');
  const propriedadeId = currentPropriedade?.id;

  const [data, setData] = useState<Date | null>(null);
  const [proximaVisita, setProximaVisita] = useState<Date | null>(null);
  const [created, setCreated] = useState<RelatorioVisitaCreated>();

  useEffect(() => {
    if (!data) {
      setProximaVisita(null);
      return;
    }

    setProximaVisita(
      (current) =>
        current ?? dayjs(data).add(DEFAULT_PROXIMA_VISITA_DIAS, 'day').toDate(),
    );
  }, [data]);

  const { data: propriedade } = useAsyncFetch(
    {
      callback: async () => {
        if (!propriedadeId) return undefined;
        const propriedades = await PropriedadesService.list();
        return propriedades.find((item) => item.id === propriedadeId);
      },
      errorMessage: 'Falha ao carregar a propriedade.',
      withLoader: true,
    },
    [propriedadeId],
  );

  const { data: safras } = useAsyncFetch(
    {
      callback: () => SafrasService.list({ ativa: true }),
      errorMessage: 'Falha ao carregar as safras.',
      withLoader: true,
    },
    [],
  );

  const currentSafraId = useMemo(
    () => safras?.find((item) => item.current)?.id ?? safras?.[0]?.id,
    [safras],
  );

  const [selectedSafraId, setSelectedSafraId] = useState<number>();

  useEffect(() => {
    if (currentSafraId !== undefined && selectedSafraId === undefined) {
      setSelectedSafraId(currentSafraId);
    }
  }, [currentSafraId, selectedSafraId]);

  const { data: datasComRegistro } = useAsyncFetch(
    {
      callback: async () => {
        if (!propriedadeId || !selectedSafraId) return [] as string[];
        return RelatorioVisitaService.operacaoDatas({
          propriedade: propriedadeId,
          safra: selectedSafraId,
          tipo: OPERACAO_DATAS_TIPOS,
        });
      },
      errorMessage: 'Falha ao carregar datas de operações de campo.',
      withLoader: true,
    },
    [propriedadeId, selectedSafraId],
  );

  const annotatedDates = useMemo(
    () => (datasComRegistro ?? []).map((item) => dayjs(item).toDate()),
    [datasComRegistro],
  );

  const glebas = propriedade?.glebas ?? [];
  const canSubmit = Boolean(
    data && propriedade && selectedSafraId && glebas.length,
  );

  const onSubmit = async () => {
    if (!canSubmit || !data || !propriedade || !selectedSafraId) return;

    const loaderId = Agro1Loader.show();

    try {
      const result = await RelatorioVisitaService.create({
        propriedadeId: propriedade.id,
        safraId: selectedSafraId,
        data: dayjs(data).format(DATE_KEY_FORMAT),
        glebaIds: glebas.map((gleba) => gleba.id),
        situacaoGeral: null,
        proximaVisita: proximaVisita
          ? dayjs(proximaVisita).format(DATE_KEY_FORMAT)
          : null,
      });

      setCreated(result);

      Agro1Toast.show({
        detail: 'Relatório de visita gerado com sucesso.',
        severity: 'success',
      });
    } catch (error) {
      Agro1Toast.show({
        detail: getCreateErrorMessage(error),
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }
  };

  const publicUrl = created
    ? `${window.location.origin}${APP_BASENAME}/relatorio-visita-aqila/${created.uuid}`
    : undefined;

  const whatsappHref = created
    ? `https://wa.me/?text=${encodeURIComponent(
        `Relatório de Visita AQILA — ${
          propriedade?.descricao ?? ''
        }: ${publicUrl}`,
      )}`
    : undefined;

  return (
    <div className="rv-create-page">
      <div className="rv-create-breadcrumb">
        <FileText className="rv-create-crumb-active" size={16} />
        <button
          className="rv-create-link"
          type="button"
          onClick={() => navigate('/relatorio-visita')}
        >
          Relatórios
        </button>
        <ChevronRight className="rv-create-crumb-sep" size={16} />
        <span className="rv-create-crumb-current">Novo Relatório</span>
      </div>

      <div className="rv-create-content">
        <div className="rv-create-intro">
          <h1 className="rv-create-title">Relatório de Visita</h1>
          <p className="rv-create-subtitle">
            Selecione uma data ou{' '}
            <button
              className="rv-create-link"
              type="button"
              onClick={() => navigate('/relatorio-visita')}
            >
              vá para a lista de relatórios já gerados
            </button>
            .
          </p>
        </div>

        {created ? (
          <div className="rv-create-card rv-create-card--center">
            <p className="rv-create-card-text">
              O relatório está disponível pelo link público abaixo.
            </p>
            <div className="rv-create-card-actions">
              <AqilaButton asChild>
                <Link
                  rel="noreferrer"
                  target="_blank"
                  to={`/relatorio-visita-aqila/${created.uuid}`}
                >
                  Abrir relatório
                </Link>
              </AqilaButton>
              <AqilaButton asChild variant="outline">
                <a href={whatsappHref} rel="noreferrer" target="_blank">
                  Compartilhar no WhatsApp
                </a>
              </AqilaButton>
              <AqilaButton
                type="button"
                variant="ghost"
                onClick={() => setCreated(undefined)}
              >
                Gerar outro
              </AqilaButton>
            </div>
          </div>
        ) : (
          <>
            {!propriedadeId ? (
              <div className="rv-create-warning">
                Selecione uma propriedade no topo para gerar o relatório.
              </div>
            ) : null}

            <div className="rv-create-card">
              <label className="rv-create-label" htmlFor="safra">
                Safra
              </label>
              <Select
                value={selectedSafraId ? String(selectedSafraId) : ''}
                onValueChange={(value: string) => {
                  setSelectedSafraId(Number(value));
                  setData(null);
                }}
              >
                <SelectTrigger
                  id="safra"
                  aria-label="Safra"
                  className="rv-create-select"
                >
                  <SelectValue placeholder="Selecione a safra" />
                </SelectTrigger>
                <SelectContent
                  className="rv-create-select-content"
                  position="popper"
                  align="start"
                  collisionPadding={{ top: 64 }}
                >
                  {(safras ?? []).map((item) => (
                    <SelectItem
                      key={item.id}
                      className="rv-create-select-item"
                      value={String(item.id)}
                    >
                      {item.descricao}
                      {item.current ? SAFRA_CURRENT_SUFFIX : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="rv-create-divider" />

              <label className="rv-create-label" htmlFor="data-visita">
                Data da visita
              </label>
              <DateField
                annotatedDates={annotatedDates}
                restrict
                value={data}
                onChange={setData}
              />
              <small className="rv-create-hint">
                Clique no ícone para abrir o calendário. Datas com anotações
                ficam destacadas.
              </small>

              <div className="rv-create-divider" />

              <label className="rv-create-label" htmlFor="proxima-visita">
                Próxima visita
              </label>
              <DateField value={proximaVisita} onChange={setProximaVisita} />
              <small className="rv-create-hint">
                Padrão: data da visita + 7 dias. Você pode alterar.
              </small>
            </div>

            <div className="rv-create-footer">
              <button
                className="rv-create-back-btn"
                type="button"
                onClick={() => navigate('/relatorio-visita')}
              >
                <ChevronLeft size={16} />
                Voltar para a lista
              </button>
              <AqilaButton
                disabled={!canSubmit}
                type="button"
                onClick={onSubmit}
              >
                Iniciar geração
              </AqilaButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RelatorioVisitaCreatePage;
